import { useMemo } from 'react';
import { Input } from '..';
import styles from './combobox.module.css';

export type Item = string | { value?: string; label?: React.ReactNode };
export type Items = ReturnType<typeof getItems>;

const VOID_FN = () => {};
const NO_ITEMS: Item[] = []; // Make constant for useMemo

// Helper to sanitize items to always have the format { value: string; label: React.ReactNode }
export const getItems = (items: Item | Item[] = NO_ITEMS) =>
  (Array.isArray(items) ? items : [items]).map((item: Item) =>
    typeof item === 'string'
      ? { value: item, label: item }
      : { label: item.label || item.value || '', value: item.value || '' }
  );

// Sanitize multiple items, and memoize for performance
export const useItems = (items: Item | Item[] = NO_ITEMS) =>
  useMemo(() => getItems(items), [items]);

// We want to measure how large the content is, in a as-close-as-possible environemnt as the real Combobox,
// but with a clone appended to document.body to ensure the Combobox to be visible (JS needs the element to be visible to measue size).
// We have no guarantee that the original Combobox is visible, as it can for instance placed in a Modal, Tab, etc.
export const getContentSize = (self?: HTMLElement | null) => {
  if (!self) return '';
  const clone = document.body.appendChild(self.cloneNode(true)) as HTMLElement;
  const list = clone.querySelector('u-datalist');
  const options = Array.from(list?.options || []);
  const option = options[0];

  if (list && option) {
    option.style.display = list.style.display = 'inline-block'; // Make visible as size of content
    option.style.whiteSpace = 'pre'; // Respect newlines
    option.textContent = options.map(({ text }) => text).join('\n');
  }
  const width = option?.scrollWidth || 0; // Measure size
  document.body.removeChild(clone); // Clean up
  return width ? `${width + 5}px` : null; // + 5px for safety, but fallback to null
};

export const filterRef = (el: HTMLElement | null) =>
  el?.setAttribute('aria-label', `Slett ${el.innerText?.trim()}`); // TODO: Translate "Slett"

export const onFilterClickToFocus = (event: React.MouseEvent<HTMLElement>) =>
  event.currentTarget.focus();

export const onKeyDown = (
  event: React.KeyboardEvent<HTMLSpanElement>,
  update: (remove?: number) => void
) => {
  const { key, currentTarget: self, target } = event;
  const filters = self.querySelectorAll<HTMLElement>(`.${styles.filter}`);
  const input = self.querySelector('input') as HTMLInputElement;
  const items = [input, ...filters];
  const index = items.indexOf(target as HTMLElement);
  const isEdgeStart = index || !input.selectionEnd; // Filter or caret is in start
  const isEdgeEnd = index || input.selectionStart === input.value.length; // Filter or caret is in end
  let next = -1;

  if (index === -1) return; // No input or filter focused
  if (key === 'Enter') event.preventDefault(); // Always prevent submit
  if (key === 'ArrowLeft' && isEdgeStart) next = (index || items.length) - 1;
  if (key === 'ArrowRight' && isEdgeEnd) next = (index + 1) % items.length;
  if ((key === 'Backspace' || key === 'Enter' || key === ' ') && index) {
    update(index - 1); // index - 1 because we prefixed items array with input
    next = 0; // If we remove a filter, move focus to input
  }
  if (!index) {
    if (key === 'Backspace' && !input.selectionEnd) next = items.length - 1;
    if (key === 'Enter' || key === ',') update();
  }
  if (items[next]) {
    event.preventDefault(); // Prevent <datalist> moving focus
    items[next].focus();
  }
};

export const getCombobox = (el?: Element | null) =>
  el?.closest<HTMLElement>(`.${styles.combobox}`);

export const getInput = (el?: Element | null) =>
  getCombobox(el)?.querySelector('input');

export const createVisualCheckbox = (checked: boolean) => (
  <Input
    className={styles.checkbox}
    aria-hidden="true" // Hide from screen reader
    aria-label="true" // Prevent accessibility checks from complaining about missing label
    checked={checked}
    onChange={VOID_FN} // Prevent React from complaining about missing onChange handler
    tabIndex={-1} // Hide from keyboard navigation
    tracking={false}
    type="checkbox"
  />
);

type SelectProps = React.ComponentPropsWithoutRef<'select'> & { values: Items };
export const createHiddenSelect = ({ values, ...rest }: SelectProps) => (
  <select
    hidden
    multiple
    onChange={VOID_FN} // Prevent React from complaining about missing onChange handler
    value={values.map(({ value }) => value)}
    {...rest}
  >
    {values.map(({ value }, index) => (
      <option key={`${index}-${value}`}>{value}</option>
    ))}
  </select>
);
